import useReportSort from '../../components/reports/useReportSort';
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiFileText,
  FiFilter,
  FiLoader,
  FiPercent,
  FiRefreshCw,
  FiSearch,
  FiShield,
} from "react-icons/fi";
import ReportFilterModal from "../../components/reports/ReportFilterModal";
import SearchableSelect from "../../components/reports/SearchableSelect";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";

const dateOnly = (value) => new Date(value).toISOString().slice(0, 10);
const firstDay = () => {
  const date = new Date();
  date.setDate(1);
  return dateOnly(date);
};
const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const text = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();
const displayDate = (value) =>
  value
    ? new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(new Date(value))
    : "—";

const initialFilters = () => ({
  from: firstDay(),
  to: dateOnly(new Date()),
  storeId: "",
  search: "",
});

function downloadFile(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function Field({ label, children }) {
  return (
    <label className="block text-sm font-semibold text-slate-700">
      {label}
      <div className="mt-2">{children}</div>
    </label>
  );
}

function Metric({ label, value, tone = "text-slate-950" }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className={`mt-1 text-xl font-bold ${tone}`}>{value}</p>
    </div>
  );
}

export default function SalesTaxRegisterPage() {
  const { accessToken, user } = useAuth();
  const canGenerate = hasPermission(
    user,
    "reports.sales_tax_register.generate",
  );
  const canExport = hasPermission(user, "reports.sales_tax_register.export");
  const [stores, setStores] = useState([]);
  const [rawReport, setReport] = useState(null);
  const { report, controls: sortControls } = useReportSort(rawReport, () => setPage(1));
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [showFilters, setShowFilters] = useState(false);
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  const updateDraft = (key, value) =>
    setDraftFilters((current) => ({ ...current, [key]: value }));

  const loadStores = useCallback(async () => {
    try {
      setStores(
        (await apiRequest("/business-reports/sales-detailed/stores", {
          accessToken,
        })) || [],
      );
    } catch (requestError) {
      setError(requestError.message || "Could not load report stores.");
    }
  }, [accessToken]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  const visibleRows = useMemo(() => {
    const query = text(appliedFilters.search);
    const rows = report?.rows || [];

    if (!query) return rows;

    return rows.filter((row) =>
      [row.billNumber, row.partyName]
        .filter(Boolean)
        .some((value) => text(value).includes(query)),
    );
  }, [appliedFilters.search, report]);

  const pageCount = Math.max(1, Math.ceil(visibleRows.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageRows = visibleRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const generate = async () => {
    if (!canGenerate) return;

    setLoading(true);
    setError("");
    try {
      const generated = await apiRequest(
        "/business-reports/sales-tax-register/generate",
        {
          method: "POST",
          body: {
            from: draftFilters.from,
            to: draftFilters.to,
            ...(draftFilters.storeId ? { storeId: draftFilters.storeId } : {}),
          },
          accessToken,
        },
      );
      setReport(generated);
      setAppliedFilters({ ...draftFilters });
      setPage(1);
      setShowFilters(false);
    } catch (requestError) {
      setReport(null);
      setError(
        requestError.message || "Could not generate the sales tax register.",
      );
    } finally {
      setLoading(false);
    }
  };

  const openFilters = () => {
    setDraftFilters({ ...appliedFilters });
    setShowFilters(true);
  };

  const closeFilters = () => {
    setDraftFilters({ ...appliedFilters });
    setShowFilters(false);
  };

  const exportExcel = async () => {
    if (!canExport || !report) return;

    setExporting(true);
    setError("");
    try {
      const parameters = new URLSearchParams({
        from: appliedFilters.from,
        to: appliedFilters.to,
        format: "XLSX",
      });
      if (appliedFilters.storeId) {
        parameters.set("storeId", appliedFilters.storeId);
      }
      const response = await apiRequest(
        `/business-reports/sales-tax-register/export?${parameters.toString()}`,
        { accessToken, rawResponse: true },
      );
      const fileName =
        response.headers
          .get("content-disposition")
          ?.match(/filename="?([^";]+)"?/)?.[1] ||
        "charms-sales-tax-register.xlsx";
      downloadFile(await response.blob(), fileName);
    } catch (requestError) {
      setError(
        requestError.message || "Could not export the sales tax register.",
      );
    } finally {
      setExporting(false);
    }
  };

  const grandTotal = report?.grandTotal;

  return (
    <div className="business-report">
      <header className="border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-800">
              Business reports
            </p>
            <h1 className="mt-1 text-2xl font-bold text-slate-950">
              Sales Tax Register
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Bill-level POS sales tax register in the same 3%, 5%, and 18%
              format as your accounting export.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openFilters}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-700 hover:border-primary-300 hover:text-primary-800"
            >
              <FiFilter /> Filters
            </button>
            <button
              type="button"
              onClick={generate}
              disabled={!canGenerate || loading}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiRefreshCw />
              )}
              {loading ? "Generating" : "Generate"}
            </button>
            <button
              type="button"
              onClick={exportExcel}
              disabled={!report || !canExport || exporting}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-primary-300 hover:text-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {exporting ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiDownload />
              )}
              Export Excel
            </button>
          </div>
        </div>
      </header>

      {error && (
        <div className="mx-4 mt-4 flex gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:mx-6">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {!canGenerate && (
        <div className="mx-4 mt-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 sm:mx-6">
          <FiShield /> You can view this report, but do not have permission to
          generate it.
        </div>
      )}

      {!report ? (
        <main className="pt-4">
          <section className="bg-white px-6 py-16 text-center">
            <FiPercent className="mx-auto mb-4 text-4xl text-primary-300" />
            <h2 className="text-xl font-bold text-slate-950">
              No register generated yet
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Choose the reporting dates and generate the sales tax register.
            </p>
          </section>
        </main>
      ) : (
        <main className="space-y-4 mt-4">
          {report.validation?.warnings?.map((warning) => (
            <div
              key={warning}
              className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
            >
              <FiAlertCircle className="mt-0.5 shrink-0" />
              {warning}
            </div>
          ))}

          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <Metric
              label="Bills"
              value={report.rows.length.toLocaleString("en-IN")}
            />
            <Metric
              label="Bill amount"
              value={money(grandTotal.billAmount)}
              tone="text-primary-800"
            />
            <Metric
              label="Total GST"
              value={money(
                grandTotal.totalGstOutput3 +
                  grandTotal.totalGstOutput5 +
                  grandTotal.totalGstOutput18,
              )}
              tone="text-violet-700"
            />
            <Metric
              label="Cash amount"
              value={money(grandTotal.cashAmount)}
              tone="text-emerald-700"
            />
            <Metric
              label="Card amount"
              value={money(grandTotal.creditCardAmount)}
              tone="text-sky-700"
            />
          </section>

          <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="font-bold text-slate-950">Sales Tax Register</h2>
                <p className="mt-0.5 text-sm text-slate-500">
                  {visibleRows.length.toLocaleString("en-IN")} bill(s) shown
                  from {displayDate(appliedFilters.from)} to{" "}
                  {displayDate(appliedFilters.to)}.
                </p>
              </div>
              <div className="rounded-lg bg-primary-50 px-3 py-2 text-xs font-semibold text-primary-800">
                POS bills only
              </div>
            </div>

            <div className="overflow-x-auto">
                  {sortControls}
              <table className="min-w-[3000px] w-full text-sm">
                <thead className="bg-primary-800 text-left text-[11px] font-bold uppercase tracking-wide text-white">
                  <tr>
                    {[
                      "SNO.",
                      "Bill Number",
                      "Party Name",
                      "Bill Amount",
                      "GST Sale 3% on ST Value",
                      "CGST Output 1.5%",
                      "SGST Output 1.5%",
                      "Total GST Output 3%",
                      "Total GST Sale 3%",
                      "GST Sale 5% on ST Value",
                      "SGST Output 2.5%",
                      "CGST Output 2.5%",
                      "Total GST Output 5%",
                      "Total GST Sale 5%",
                      "GST Sale 18% on ST Value",
                      "SGST Output 9%",
                      "CGST Output 9%",
                      "Total GST Output 18%",
                      "Total GST Sale 18%",
                      "Cash Amount",
                      "Credit Amount",
                      "Credit Card Amount",
                    ].map((column) => (
                      <th
                        key={column}
                        className="whitespace-nowrap px-3 py-3.5"
                      >
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row) => (
                    <SalesTaxRow key={row.billNumber} row={row} />
                  ))}
                  {!pageRows.length && (
                    <tr>
                      <td
                        colSpan="22"
                        className="px-4 py-16 text-center text-sm text-slate-500"
                      >
                        No sales tax bills match the generated filters.
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <SalesTaxRow
                    row={{ partyName: "Grand Totals", ...grandTotal }}
                    total
                  />
                </tfoot>
              </table>
            </div>

            <div className="flex flex-col gap-4 border-t border-slate-200 px-4 py-4 sm:px-5 md:flex-row md:items-center md:justify-between">
              <p className="text-sm text-slate-500">
                Showing{" "}
                {visibleRows.length ? (currentPage - 1) * pageSize + 1 : 0}–
                {Math.min(currentPage * pageSize, visibleRows.length)} of{" "}
                {visibleRows.length} bill(s)
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
                  Rows
                  <select
                    value={pageSize}
                    onChange={(event) => {
                      setPageSize(Number(event.target.value));
                      setPage(1);
                    }}
                    className="rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-sm outline-none focus:border-primary-700"
                  >
                    {[25, 50, 100, 200, 300].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  disabled={currentPage === 1}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <FiChevronLeft /> Previous
                </button>
                <span className="text-sm font-semibold text-slate-700">
                  {currentPage} / {pageCount}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setPage((current) => Math.min(pageCount, current + 1))
                  }
                  disabled={currentPage === pageCount}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next <FiChevronRight />
                </button>
              </div>
            </div>
          </section>

          {!canExport && (
            <p className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <FiShield /> You can preview this register, but do not have export
              permission.
            </p>
          )}
        </main>
      )}

      <ReportFilterModal
        open={showFilters}
        title="Sales tax register filters"
        description="Choose dates, godown/store, and an optional bill or party search. Generate applies the filters and closes this window."
        loading={loading}
        onApply={generate}
        onClose={closeFilters}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="From date">
            <input
              type="date"
              value={draftFilters.from}
              onChange={(event) => updateDraft("from", event.target.value)}
              className="report-input"
            />
          </Field>
          <Field label="To date">
            <input
              type="date"
              value={draftFilters.to}
              onChange={(event) => updateDraft("to", event.target.value)}
              className="report-input"
            />
          </Field>
          <Field label="Godown / Store">
            <SearchableSelect
              value={draftFilters.storeId}
              onChange={(value) => updateDraft("storeId", value)}
              placeholder="All stores"
              options={stores.map((store) => ({
                value: store.id,
                label: `${store.name} (${store.code})`,
              }))}
            />
          </Field>
          <Field label="Search bill or party">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={draftFilters.search}
                onChange={(event) => updateDraft("search", event.target.value)}
                placeholder="Bill number or party name"
                className="report-input pl-9"
              />
            </div>
          </Field>
        </div>
      </ReportFilterModal>
    </div>
  );
}

function SalesTaxRow({ row, total = false }) {
  const monetaryKeys = [
    "billAmount",
    "gstSale3OnStValue",
    "cgstOutput15",
    "sgstOutput15",
    "totalGstOutput3",
    "totalGstSale3",
    "gstSale5OnStValue",
    "sgstOutput25",
    "cgstOutput25",
    "totalGstOutput5",
    "totalGstSale5",
    "gstSale18OnStValue",
    "sgstOutput9",
    "cgstOutput9",
    "totalGstOutput18",
    "totalGstSale18",
    "cashAmount",
    "creditAmount",
    "creditCardAmount",
  ];

  return (
    <tr
      className={
        total
          ? "border-t-2 border-primary-200 bg-primary-50 font-bold text-primary-950"
          : "border-t border-slate-100 hover:bg-primary-50/30"
      }
    >
      <td className="px-3 py-3 text-slate-500">
        {total ? "" : row.serialNumber}
      </td>
      <td className="whitespace-nowrap px-3 py-3 font-semibold text-primary-800">
        {total ? "" : row.billNumber}
      </td>
      <td className="whitespace-nowrap px-3 py-3">{row.partyName}</td>
      {monetaryKeys.map((key) => (
        <td
          key={key}
          className="whitespace-nowrap px-3 py-3 text-right tabular-nums"
        >
          {Number(row[key]) ? (/^(cgst|sgst|igst)/.test(key) ? `₹${Number(row[key]).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}` : money(row[key])) : ""}
        </td>
      ))}
    </tr>
  );
}
